import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmployerCourse } from "@services/all.services";
import Avatar from "@components/tailwindComponents/Avatar";
import CheckboxSimple from "@components/tailwindComponents/CheckboxSimple";
import { Controller, useWatch } from "react-hook-form";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { useAccessFolderEnterprise } from "@hooks/employeFolder/useAccessFolderEnterprise.js";
import { SectionHolidayTableBulkCells } from "./container/JobAndSalary/SectionHolidayTableBulk";
import { SectionPersonnalInfoTableBulkCells } from "./container/PersonnalInformations/SectionPersonnalInfoTableBulk";
import { SectionEmailAndPhoneTableBulkCells } from "./container/PersonnalInformations/SectionEmailAndPhoneTableBulk";
import { SectionAdressTableBulkCells } from "./container/PersonnalInformations/SectionAdressTableBulk";
import { SectionHealthTableBulkCells } from "./container/PersonnalInformations/SectionHealthTableBulk";
import { SectionClothesTableBulkCells } from "./container/PersonnalInformations/SectionClothesTableBulk";
import { SectionFirstEmergencyTableBulkCells } from "./container/PersonnalInformations/SectionFirstEmergencyTableBulk";
import { SectionSecondEmergencyTableBulkCells } from "./container/PersonnalInformations/SectionSecondEmergencyTableBulk";
import { SectionJobTableBulkCells } from "./container/JobAndSalary/SectionJobTableBulk";
import { SectionSecurityTableBulkCells } from "./container/JobAndSalary/SectionSecurityTableBulk";
import { SectionInsuranceBankingTableBulkCells } from "./container/JobAndSalary/SectionInsuranceBankingTableBulk";
import { SectionCourseAndSalaryTableBulkCells } from "./container/JobAndSalary/SectionCourseAndSalaryTableBulk";
import { SectionAdditionalInfoTableBulkCells } from "./container/PersonnalInformations/SectionAdditionalInfoTableBulk";

const ItemBulkEditRows = ({
  t,
  employee,
  employeeIndex,
  useTimeBank,
  mustWait,
  control,
  isSubmitted,
  trigger,
  getValues,
  setValue,
  canSeeEmployeFolder,
  isNotDeniedEmployer,
  haveAccessFolderEnterprise,
  showError,
  showWarning,
  isActiveBankingInformationAndInsuranceAccess,
  watch,
  maxAdvantageTypes,
  hiddenColumns = {}
}) => {
  const employers = useReduxSelector(({ commonData }) => commonData.employers)
  const emplacements = useReduxSelector(({ commonData }) => commonData.emplacements)
  const permissions = useReduxSelector(({ commonData }) => commonData.permissions)
  const postes = useReduxSelector(({ commonData }) => commonData.postes)
  const groupes = useReduxSelector(({ commonData }) => commonData.groupes)
  const [careerList, setCareerList] = useState([]);
  const isLastAdmin = useMemo(
    () => employers.some((emp) => emp.id === employee._id && emp.lastAdmin),
    [employee._id, employers]
  )
  const [superiorReadonly, setSuperiorReadonly] = useState(false)
  const { isReadOnlySection } = useAccessFolderEnterprise()
  const rowSnapshotRef = useRef(null)

  const serializeRowForAutoSelect = useCallback((rowValue) => {
    if (!rowValue) return "";

    const { selected, ...rest } = rowValue;
    return JSON.stringify(rest);
  }, []);

  useEffect(() => {
    const fetchCareerData = async () => {
      if (!employee?._id) return;

      try {
        const response = await EmployerCourse.getByEmployer(employee._id);
        setCareerList(response?.data?.data?.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement des données de carrière:", error);
        setCareerList([]);
      }
    };

    fetchCareerData();
  }, [employee?._id]);

  useEffect(() => {
    const hierarchicalId = employee?.emploiSalary?.job?.hierarchical?.id;
    let disable = false;

    if (!hierarchicalId) {
      disable = true;
    } else {
      const superior = employers.find((e) => e.id === hierarchicalId);
      if (!superior) {
        disable = true;
      }
    }

    setSuperiorReadonly(disable);
  }, [employee?.emploiSalary?.job?.hierarchical?.id, employers]);

  const holidayWatcher = useWatch({
    control,
    name: `employees.${employeeIndex}.holiday`,
  })
  const holidayRemainingWatcher = useWatch({
    control,
    name: `employees.${employeeIndex}.holidayRemaining`,
  })
  const rowWatcher = useWatch({
    control,
    name: `employees.${employeeIndex}`,
  })

  useEffect(() => {
    const currentSnapshot = serializeRowForAutoSelect(rowWatcher);

    if (rowSnapshotRef.current === null) {
      rowSnapshotRef.current = currentSnapshot;
      return;
    }

    if (mustWait || getValues(`employees.${employeeIndex}.selected`)) {
      rowSnapshotRef.current = currentSnapshot;
      return;
    }

    if (rowSnapshotRef.current !== currentSnapshot) {
      rowSnapshotRef.current = currentSnapshot;
      setValue(`employees.${employeeIndex}.selected`, true, {
        shouldDirty: true
      });
    }
  }, [employeeIndex, getValues, mustWait, rowWatcher, serializeRowForAutoSelect, setValue]);

  const isCellVisible = useCallback((sectionKey) => {
    if (!employee?.visibleSections) return false;

    return (
      employee.visibleSections?.jobSalaryHolidays?.includes(sectionKey) ||
      employee.visibleSections?.personalInfo?.includes(sectionKey)
    );
  }, [employee?.visibleSections]);

  useEffect(() => {
    if (isSubmitted) {
      const isSelected = getValues(`employees.${employeeIndex}.selected`);

      if (isSelected) {
        if (isCellVisible("hoursPerDayAndBalance")) {
          trigger(`employees.${employeeIndex}.holidayRemaining`);
          trigger(`employees.${employeeIndex}.holiday.hoursPerDay`);
        }
      }
    }
  }, [
    holidayWatcher,
    holidayRemainingWatcher,
    trigger,
    isSubmitted,
    employeeIndex,
    getValues,
    isCellVisible,
  ]);

  if (!canSeeEmployeFolder) {
    return (
      <tr className="arh-sticky arh-w-full arh-gap-6 arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyLightColor">
        <td className="arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-sticky arh-left-0 arh-bg-white arh-z-[5]">
          <div className="arh-flex arh-items-center">
            <CheckboxSimple
              onClick={() => { }}
              haveHover={false}
              checked={false}
              variant="green"
              className="arh-mr-2"
              disabled={mustWait}
            />
            <Avatar
              src={employee.avatar}
              name={employee.name}
              textStyle="arh-font-medium arh-text-xs arh-text-greyDisabledText"
            />
          </div>
        </td>
        <td colSpan={20} className="arh-p-4 arh-font-poppins arh-text-xs arh-text-greyDisabledText">
          {t("L'accès aux dossiers employés n'est pas autorisé")}
        </td>
      </tr>
    );
  }

  if (!isNotDeniedEmployer(employee._id)) {
    return (
      <tr className="arh-w-full arh-gap-6 arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyLightColor">
        <td className="arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-sticky arh-left-0 arh-bg-white arh-z-[5]">
          <div className="arh-flex arh-items-center">
            <CheckboxSimple
              onClick={() => showError("L'accès au dossier de cet employé est verrouillé")}
              haveHover={false}
              checked={false}
              variant="green"
              className="arh-mr-2"
              disabled={mustWait}
            />
            <Avatar
              src={employee.avatar}
              name={employee.name}
              textStyle="arh-font-medium arh-text-xs arh-text-greyDisabledText"
            />
          </div>
        </td>
        <td colSpan={20} className="arh-p-4 arh-font-poppins arh-text-xs arh-text-greyDisabledText">
          {t("L'accès au dossier de cet employé est verrouillé")}
        </td>
      </tr>
    );
  }

  if (!haveAccessFolderEnterprise(employee._id)) {
    return (
      <tr className="arh-w-full arh-gap-6 arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyLightColor">
        <td className="arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-sticky arh-left-0 arh-bg-white arh-z-[5]">
          <div className="arh-flex arh-items-center">
            <CheckboxSimple
              onClick={() => showWarning("Accès limité Veuillez demander l'accès à votre administrateur")}
              haveHover={false}
              checked={false}
              variant="green"
              className="arh-mr-2"
              disabled={mustWait}
            />
            <Avatar
              src={employee.avatar}
              name={employee.name}
              textStyle="arh-font-medium arh-text-xs arh-text-greyDisabledText"
            />
          </div>
        </td>
        <td colSpan={20} className="arh-p-4 arh-font-poppins arh-text-xs arh-text-greyDisabledText">
          {t("Accès limité - Vous n'avez pas les autorisations nécessaires pour voir les données de cet employé")}
        </td>
      </tr>
    );
  }

  return (
    <tr
      className="arh-w-full arh-gap-6 arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyLightColor"
    >
      <Controller
        key="hidden-id"
        control={control}
        name={`employees.${employeeIndex}.id`}
        defaultValue={employee.id}
        render={({ field }) => <input {...field} type="hidden" />}
      />
      <Controller
        key="hidden-emploiSalaryId"
        control={control}
        name={`employees.${employeeIndex}.emploiSalary._id`}
        defaultValue={employee.emploiSalary?._id || null}
        render={({ field }) => <input {...field} type="hidden" />}
      />
      <td
        key="employee"
        className={`
          arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor 
          arh-sticky arh-left-0 arh-bg-white arh-z-[5]
          ${isCellVisible("salaryAndAdvantages") ? "arh-align-top arh-pt-6" : "arh-align-middle"}
        `}
      >
        <div className="arh-flex arh-items-center">
          <Controller
            control={control}
            name={`employees.${employeeIndex}.selected`}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <CheckboxSimple
                id={`employee-check-${employee.id}`}
                name={`employee-${employee.id}`}
                onClick={() => {
                  if (isCellVisible("bankingAndInsuranceInformation") && !isActiveBankingInformationAndInsuranceAccess) {
                    return showWarning(
                      "Accès limité aux informations bancaires et d'assurance Veuillez demander l'accès à votre administrateur"
                    )
                  }
                  onChange(!value);
                }}
                haveHover={false}
                checked={value}
                variant="green"
                className="arh-mr-2"
                disabled={mustWait}
              />
            )}
          />
          <Avatar
            src={employee.avatar}
            name={employee.name}
            textStyle="arh-font-medium arh-text-xs arh-text-darkGreyColor"
          />
        </div>
      </td>
      {isCellVisible("hoursPerDayAndBalance") && (
        <SectionHolidayTableBulkCells
          key="holiday-cells"
          employee={employee}
          employeeIndex={employeeIndex}
          useTimeBank={useTimeBank}
          mustWait={mustWait}
          readOnly={isReadOnlySection("DListEmploisalarySectionHolidays")}
          control={control}
          getValues={getValues}
          setValue={setValue}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("personalInfos") && (
        <SectionPersonnalInfoTableBulkCells
          key="personal-info-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopers")}
          mustWait={mustWait}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("emailsAndPhones") && (
        <SectionEmailAndPhoneTableBulkCells
          key="email-phone-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionEmailsAndPhones")}
          mustWait={mustWait}
          getValues={getValues}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("address") && (
        <SectionAdressTableBulkCells
          key="address-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionAdress")}
          setValue={setValue}
          getValues={getValues}
          mustWait={mustWait}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("healthAllergiesFoodPrefs") && (
        <SectionHealthTableBulkCells
          key="health-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionHealthAndFoods")}
          mustWait={mustWait}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("clothingSize") && (
        <SectionClothesTableBulkCells
          key="clothes-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionClothingSizes")}
          mustWait={mustWait}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("firstContact") && (
        <SectionFirstEmergencyTableBulkCells
          key="first-emergency-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionFirstEmergencyContact")}
          mustWait={mustWait}
          getValues={getValues}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("secondContact") && (
        <SectionSecondEmergencyTableBulkCells
          key="second-emergency-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListInfopersSectionSecondEmergencyContact")}
          mustWait={mustWait}
          getValues={getValues}
          hiddenColumns={hiddenColumns}
        />
      )}
      {
        isCellVisible("additionalInformation") && (
          <SectionAdditionalInfoTableBulkCells
            key="additional-info-cells"
            control={control}
            employeeIndex={employeeIndex}
            employee={employee}
            readOnly={isReadOnlySection("DListInfopersSectionAdditionalInformation")}
            mustWait={mustWait}
            hiddenColumns={hiddenColumns}
          />
        )
      }
      {isCellVisible("job") && (
        <SectionJobTableBulkCells
          key="job-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListEmploisalarySectionJob")}
          employeList={employers}
          emplacementsList={emplacements}
          groupesList={groupes}
          postes={postes}
          superiorReadonly={superiorReadonly}
          trigger={trigger}
          mustWait={mustWait}
          getValues={getValues}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("security") && (
        <SectionSecurityTableBulkCells
          key="security-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          permissionsList={permissions}
          readOnly={isReadOnlySection("DListEmploisalarySectionJob")}
          mustWait={mustWait}
          isLastAdmin={isLastAdmin}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("salaryAndAdvantages") && (
        <SectionCourseAndSalaryTableBulkCells
          key="salary-and-advantages-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListEmploisalarySectionSalaryAndAdvantages")}
          mustWait={mustWait}
          getValues={getValues}
          setValue={setValue}
          careerList={careerList}
          watch={watch}
          maxAdvantageTypes={maxAdvantageTypes}
          hiddenColumns={hiddenColumns}
        />
      )}
      {isCellVisible("bankingAndInsuranceInformation") && (
        <SectionInsuranceBankingTableBulkCells
          key="banking-insurance-cells"
          control={control}
          employeeIndex={employeeIndex}
          employee={employee}
          readOnly={isReadOnlySection("DListEmploisalarySectionBankingAndInsuranceInformation")}
          mustWait={mustWait}
          getValues={getValues}
          isActiveBankingInformationAndInsuranceAccess={isActiveBankingInformationAndInsuranceAccess}
          hiddenColumns={hiddenColumns}
        />
      )}
    </tr>
  );
}

export default memo(ItemBulkEditRows);
